import FaceTecSDK, {
  FaceTecSessionRequestProcessor,
  FaceTecSessionResult,
} from "facetec-sdk";
import FaceTecSessionError from "./FaceTecSessionError";

// The response blob to pass to the SDK, or `undefined` to cancel the session.
export type Effect = { blob: string } | undefined;

export type StepRequestHandler = (requestBlob: string) => Promise<Effect>;

export type OnFaceTecSessionRequest =
  FaceTecSessionRequestProcessor["onSessionRequest"];
export type OnFaceTecExit = FaceTecSessionRequestProcessor["onFaceTecExit"];

// Makes the session request handler for a single session.
//
// Once the session is aborted, the SDK still sends a request to report the
// exit; it and any other request after the abort are aborted without being
// handled, so they don't reach the steps, which have already ended.
export const makeOnSessionRequest = (
  handleStepRequest: StepRequestHandler,
  onError: (error: Error) => void,
): OnFaceTecSessionRequest => {
  let aborted = false;

  return (requestBlob, callback) => {
    if (aborted) return callback.abortOnCatastrophicError();

    handleStepRequest(requestBlob).then(
      (effect) => {
        if (!effect) {
          aborted = true;
          return callback.abortOnCatastrophicError();
        }

        return callback.processResponse(effect.blob);
      },
      (error) => {
        console.log("[facetec session] session request failed", error);
        aborted = true;
        onError(error);
        callback.abortOnCatastrophicError();
      },
    );
  };
};

export const makeOnFaceTecExit =
  (onDone: () => void, onError: (error: Error) => void): OnFaceTecExit =>
  (result: FaceTecSessionResult) => {
    const { status } = result;
    console.log(
      "[facetec session] exit",
      FaceTecSDK.FaceTecSessionStatus[status],
      result,
    );

    if (
      status === FaceTecSDK.FaceTecSessionStatus.SessionCompleted ||
      // The session was cancelled by the step request handler.
      status === FaceTecSDK.FaceTecSessionStatus.RequestAborted
    ) {
      onDone();
      return;
    }

    onError(new FaceTecSessionError(status));
  };
